import React from "react";
import PropTypes from "prop-types";

import { formatDate } from "utils";

import Money from "components/Money";

import { MailToMessage } from "scenes/Authentication/components/Layout/components";

function SubscriptionBillingInfo({
  status,
  currency,
  nextBillingPrice,
  billingPeriod,
  nextBillingDate,
  trialEndDate,
  cancellationDate,
}) {
  const getFormattedDate = (date) => {
    const dateFormatter = formatDate("simpleDate");

    return <b>{dateFormatter(date)}</b>;
  };

  const getFormattedPrice = () => {
    return <Money currency={currency}>{nextBillingPrice}</Money>;
  };

  const getBillingInfo = () => {
    switch (status) {
      case "Cancelled":
      case "Non Renewing":
        if (!cancellationDate) {
          return (
            <React.Fragment>
              We are unable to check your subscription cancellation date at the
              moment. Please retry or contact support for assistance.
            </React.Fragment>
          );
        }

        return (
          <React.Fragment>
            We&apos;ll miss you! Your subscription is set to end on{" "}
            {getFormattedDate(cancellationDate)}, and you&apos;ll lose access to
            your account after that. If the winds of change lead you back,
            remember you can reactivate your subscription anytime by reaching
            out to our{" "}
            <MailToMessage subject="Reactivate my Hubly account">
              support
            </MailToMessage>
            .
          </React.Fragment>
        );
      case "Active":
      case "Pending Changes":
        if (!nextBillingDate || !nextBillingPrice) {
          return (
            <React.Fragment>
              We are unable to check{" "}
              {!nextBillingDate
                ? "when your subscription is set to renew"
                : "what's your next bill price"}{" "}
              at the moment. Please retry or contact support for assistance.
            </React.Fragment>
          );
        }

        return (
          <React.Fragment>
            Your subscription is set to renew on{" "}
            {getFormattedDate(nextBillingDate)} and you will be charged{" "}
            {getFormattedPrice()} (plus tax).
          </React.Fragment>
        );
      case "Free Trial":
        if (!trialEndDate || !nextBillingPrice) {
          return (
            <React.Fragment>
              We are unable to check{" "}
              {!trialEndDate
                ? "when your free trial will end"
                : "what's your bill price"}{" "}
              at the moment. Please retry or contact support for assistance.
            </React.Fragment>
          );
        }

        return (
          <React.Fragment>
            Your Free Trial ends on {getFormattedDate(trialEndDate)} and,
            starting then, you will be charged {getFormattedPrice()}{" "}
            {billingPeriod} (plus tax).
          </React.Fragment>
        );
      default:
        return null;
    }
  };

  return (
    <span data-test="subscription-billing-info" style={{ color: "#666666" }}>
      {getBillingInfo()}
    </span>
  );
}

SubscriptionBillingInfo.propTypes = {
  status: PropTypes.oneOf([
    "Free Trial",
    "Active",
    "Cancelled",
    "Pending Changes",
    "Non Renewing",
  ]).isRequired,
  currency: PropTypes.string.isRequired,
  billingPeriod: PropTypes.string,
  nextBillingPrice: PropTypes.string,
  nextBillingDate: PropTypes.instanceOf(Date),
  trialEndDate: PropTypes.instanceOf(Date),
  cancellationDate: PropTypes.instanceOf(Date),
};

SubscriptionBillingInfo.defaultProps = {
  billingPeriod: "",
  nextBillingPrice: "",
  nextBillingDate: null,
  trialEndDate: null,
  cancellationDate: null,
};

export default SubscriptionBillingInfo;
